import { camelToKebabCase } from "../../functions/camel-to-kebab-case.qwik.mjs";
function throttle(func, wait, options = {}) {
  let context;
  let args;
  let result;
  let timeout = null;
  let previous = 0;
  const later = function() {
    previous = options.leading === false ? 0 : Date.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout)
      context = args = null;
  };
  return function() {
    const now = Date.now();
    if (!previous && options.leading === false)
      previous = now;
    const remaining = wait - (now - previous);
    context = this;
    args = arguments;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(context, args);
      if (!timeout)
        context = args = null;
    } else if (!timeout && options.trailing !== false)
      timeout = setTimeout(later, remaining);
    return result;
  };
}
function assign(target, ..._args) {
  const to = Object(target);
  for (let index = 1; index < arguments.length; index++) {
    const nextSource = arguments[index];
    if (nextSource != null) {
      for (const nextKey in nextSource)
        if (Object.prototype.hasOwnProperty.call(nextSource, nextKey))
          to[nextKey] = nextSource[nextKey];
    }
  }
  return to;
}
function bindAnimations(animations) {
  for (const animation of animations)
    switch (animation.trigger) {
      case "pageLoad":
        triggerAnimation(animation);
        break;
      case "scrollInView":
        bindScrollInViewAnimation(animation);
        break;
    }
}
function warnElementNotPresent(id) {
  console.warn(`Cannot animate element: element with ID ${id} not found!`);
}
function augmentAnimation(animation, element) {
  const stylesUsed = getAllStylesUsed(animation);
  const computedStyle = getComputedStyle(element);
  const firstStyles = animation.steps[0].styles;
  const lastStyles = animation.steps[animation.steps.length - 1].styles;
  const bothStyles = [
    firstStyles,
    lastStyles
  ];
  for (const styles of bothStyles) {
    for (const style of stylesUsed)
      if (!(style in styles))
        styles[style] = computedStyle[style];
  }
}
function getAllStylesUsed(animation) {
  const properties = [];
  for (const step of animation.steps) {
    for (const key in step.styles)
      if (properties.indexOf(key) === -1)
        properties.push(key);
  }
  return properties;
}
function triggerAnimation(animation) {
  const elements = Array.prototype.slice.call(document.getElementsByClassName(animation.elementId || animation.id || ""));
  if (!elements.length) {
    warnElementNotPresent(animation.elementId || animation.id || "");
    return;
  }
  Array.from(elements).forEach((element) => {
    augmentAnimation(animation, element);
    element.style.transition = "none";
    element.style.transitionDelay = "0";
    assign(element.style, animation.steps[0].styles);
    setTimeout(() => {
      element.style.transition = `all ${animation.duration}s ${camelToKebabCase(animation.easing)}`;
      if (animation.delay)
        element.style.transitionDelay = animation.delay + "s";
      assign(element.style, animation.steps[1].styles);
      setTimeout(() => {
        element.style.transition = "";
        element.style.transitionDelay = "";
      }, (animation.delay || 0) * 1e3 + animation.duration * 1e3 + 100);
    });
  });
}
function bindScrollInViewAnimation(animation) {
  const elements = Array.prototype.slice.call(document.getElementsByClassName(animation.elementId || animation.id || ""));
  if (!elements.length) {
    warnElementNotPresent(animation.elementId || animation.id || "");
    return;
  }
  Array.from(elements).forEach((element) => {
    augmentAnimation(animation, element);
    let triggered = false;
    let pendingAnimation = false;
    function immediateOnScroll() {
      if (!triggered && isScrolledIntoView(element)) {
        triggered = true;
        pendingAnimation = true;
        setTimeout(() => {
          assign(element.style, animation.steps[1].styles);
          if (!animation.repeat)
            document.removeEventListener("scroll", onScroll);
          setTimeout(() => {
            pendingAnimation = false;
            if (!animation.repeat) {
              element.style.transition = "";
              element.style.transitionDelay = "";
            }
          }, (animation.duration + (animation.delay || 0)) * 1e3 + 100);
        });
      } else if (animation.repeat && triggered && !pendingAnimation && !isScrolledIntoView(element)) {
        triggered = false;
        assign(element.style, animation.steps[0].styles);
      }
    }
    const onScroll = throttle(immediateOnScroll, 200, {
      leading: false
    });
    function isScrolledIntoView(elem) {
      const rect = elem.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const thresholdPercent = (animation.thresholdPercent || 0) / 100;
      const threshold = thresholdPercent * windowHeight;
      return rect.bottom > threshold && rect.top < windowHeight - threshold;
    }
    const defaultState = animation.steps[0].styles;
    function attachDefaultState() {
      assign(element.style, defaultState);
    }
    attachDefaultState();
    setTimeout(() => {
      element.style.transition = `all ${animation.duration}s ${camelToKebabCase(animation.easing)}`;
      if (animation.delay)
        element.style.transitionDelay = animation.delay + "s";
    });
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true
    });
    immediateOnScroll();
  });
}
export {
  bindAnimations,
  bindScrollInViewAnimation,
  triggerAnimation
};
